import { useState, useEffect } from 'react';
import { Modal, Button } from './ui';
import { api } from '../services/api-client';
import { useAuthStore } from '@ipsoftsrl/frontend-auth';
import { PdfViewer } from '@ipsoftsrl/pdf-viewer';

interface MandatoryDoc {
  document_id: string;
  file_name: string;
  tag: string;
  tag_extra: string;
  uploaded_at: string;
  area: string;
  acknowledged: boolean;
  company_name: string | null;
}

type TabType = 'PRIVATE' | 'PUBLIC';

/**
 * Two-modal flow for mandatory documents:
 * 1. List modal (standard size) — shows pending documents in two tabs (Private / Public)
 * 2. Viewer modal (resizable) — opens PDF with scroll-to-end requirement
 */
export function MandatoryDocsModal() {
  const [docs, setDocs] = useState<MandatoryDoc[]>([]);
  const [activeTab, setActiveTab] = useState<TabType>('PRIVATE');
  const [showList, setShowList] = useState(false);
  const [viewingDoc, setViewingDoc] = useState<MandatoryDoc | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [canAcknowledge, setCanAcknowledge] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem('mandatory-docs-dismissed') === 'true');
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  useEffect(() => {
    if (isAuthenticated && !dismissed) {
      loadMandatoryDocs();
    }
  }, [isAuthenticated]);

  const loadMandatoryDocs = async () => {
    try {
      const response = await api.get<{ data: MandatoryDoc[] }>('/portals/documentale/mandatory-documents');
      const allDocs = response.data.data || [];
      const pending = allDocs.filter(d => !d.acknowledged);
      if (pending.length > 0) {
        setDocs(pending);
        // Default to tab with documents: prefer PRIVATE, fallback to PUBLIC
        const hasPrivate = pending.some(d => d.area === 'PRIVATE');
        setActiveTab(hasPrivate ? 'PRIVATE' : 'PUBLIC');
        setShowList(true);
      }
    } catch (err) {
      console.error('[MandatoryDocsModal] Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const privateDocs = docs.filter(d => d.area === 'PRIVATE');
  const publicDocs = docs.filter(d => d.area === 'PUBLIC');
  const currentDocs = activeTab === 'PRIVATE' ? privateDocs : publicDocs;

  const handleOpenDoc = async (doc: MandatoryDoc) => {
    setViewingDoc(doc);
    setLoadingPdf(true);
    setPdfUrl(null);
    setCanAcknowledge(false);
    try {
      const response = await api.get<{ url: string }>(`/portals/documentale/documents/${doc.document_id}/url`);
      setPdfUrl(response.data.url);
    } catch {
      setPdfUrl(null);
    } finally {
      setLoadingPdf(false);
    }
  };

  const handleCloseViewer = () => {
    setViewingDoc(null);
    setPdfUrl(null);
    setCanAcknowledge(false);
  };

  const handleAcknowledge = async () => {
    if (!viewingDoc) return;
    try {
      await api.post(`/portals/documentale/documents/${viewingDoc.document_id}/read`, {
        action: 'ACKNOWLEDGE',
      });
      const updated = docs.filter(d => d.document_id !== viewingDoc.document_id);
      setDocs(updated);
      setViewingDoc(null);
      setPdfUrl(null);
      setCanAcknowledge(false);
      if (updated.length === 0) {
        setShowList(false);
      }
    } catch {
      // Keep viewer open
    }
  };

  const handlePostpone = () => {
    setShowList(false);
    setDismissed(true);
    sessionStorage.setItem('mandatory-docs-dismissed', 'true');
  };

  if (loading || docs.length === 0) return null;

  return (
    <>
      {/* Modal 1: Document list with tabs */}
      <Modal
        isOpen={showList && !viewingDoc}
        onClose={handlePostpone}
        title="Documenti a lettura obbligatoria"
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <span style={{ color: '#666', fontSize: '0.875rem' }}>
              {docs.length} documento/i da leggere
            </span>
            <Button variant="ghost" size="sm" onClick={handlePostpone}>
              Leggi dopo
            </Button>
          </div>
        }
      >
        <p style={{ margin: '0 0 1rem', color: '#4a5568' }}>
          Sono presenti documenti che richiedono la tua presa visione.
        </p>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '2px solid #e2e8f0', marginBottom: '1rem' }}>
          <button
            onClick={() => setActiveTab('PRIVATE')}
            style={{
              padding: '0.5rem 1rem',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: activeTab === 'PRIVATE' ? 600 : 400,
              color: activeTab === 'PRIVATE' ? '#2563eb' : '#718096',
              borderBottom: activeTab === 'PRIVATE' ? '2px solid #2563eb' : '2px solid transparent',
              marginBottom: '-2px',
            }}
          >
            Privati {privateDocs.length > 0 && <span style={{ marginLeft: '0.25rem', background: activeTab === 'PRIVATE' ? '#dbeafe' : '#edf2f7', borderRadius: '9999px', padding: '0.1rem 0.5rem', fontSize: '0.75rem' }}>{privateDocs.length}</span>}
          </button>
          <button
            onClick={() => setActiveTab('PUBLIC')}
            style={{
              padding: '0.5rem 1rem',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: activeTab === 'PUBLIC' ? 600 : 400,
              color: activeTab === 'PUBLIC' ? '#2563eb' : '#718096',
              borderBottom: activeTab === 'PUBLIC' ? '2px solid #2563eb' : '2px solid transparent',
              marginBottom: '-2px',
            }}
          >
            Pubblici {publicDocs.length > 0 && <span style={{ marginLeft: '0.25rem', background: activeTab === 'PUBLIC' ? '#dbeafe' : '#edf2f7', borderRadius: '9999px', padding: '0.1rem 0.5rem', fontSize: '0.75rem' }}>{publicDocs.length}</span>}
          </button>
        </div>

        {/* Document list */}
        {currentDocs.length === 0 ? (
          <p style={{ color: '#a0aec0', textAlign: 'center', padding: '1rem 0' }}>
            Nessun documento {activeTab === 'PRIVATE' ? 'privato' : 'pubblico'} da leggere.
          </p>
        ) : (
          <div role="list" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {currentDocs.map((doc) => (
              <button
                key={doc.document_id}
                role="listitem"
                onClick={() => handleOpenDoc(doc)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.75rem 1rem',
                  border: '1px solid #e2e8f0',
                  borderRadius: '0.5rem',
                  background: '#fff',
                  cursor: 'pointer',
                  textAlign: 'left',
                  width: '100%',
                  transition: 'background 0.15s',
                }}
                onMouseOver={(e) => (e.currentTarget.style.background = '#f7fafc')}
                onMouseOut={(e) => (e.currentTarget.style.background = '#fff')}
              >
                <span style={{ fontSize: '1.5rem' }}>📄</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 500, color: '#1a202c' }}>
                    {doc.tag}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#718096', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {doc.company_name ? `${doc.company_name} — ` : ''}{doc.tag_extra || doc.file_name}
                  </div>
                </div>
                <span style={{ fontSize: '0.75rem', color: '#a0aec0' }}>
                  {new Date(doc.uploaded_at).toLocaleDateString('it-IT')}
                </span>
              </button>
            ))}
          </div>
        )}
      </Modal>

      {/* Modal 2: PDF Viewer (resizable) */}
      <Modal
        isOpen={!!viewingDoc}
        onClose={handleCloseViewer}
        title={viewingDoc?.tag || ''}
        resizable={true}
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Button variant="ghost" size="sm" onClick={handleCloseViewer}>
              ← Torna alla lista
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleAcknowledge}
              disabled={!canAcknowledge}
              title={!canAcknowledge ? 'Scorri fino alla fine del documento per abilitare' : ''}
            >
              {canAcknowledge ? '✔ Confermo presa visione' : '🔒 Leggi tutto il documento'}
            </Button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          {loadingPdf ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <span>Caricamento documento...</span>
            </div>
          ) : pdfUrl ? (
            <PdfViewer url={pdfUrl} onReachedEnd={() => setCanAcknowledge(true)} />
          ) : (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <span>Impossibile caricare il documento.</span>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
}
